import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Grid,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  Paper,
  Autocomplete,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  TablePagination,
} from '@mui/material';
import {
  Breadcrumb,
  CTSinput,
  CTSRinput,
  StyledTableCell,
  StyledTableRow,
} from '../../reusableComponent/Reusables';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import CloseIcon from '@mui/icons-material/Close';
import { Print } from '@mui/icons-material';
import styles from '../../../styles.module.css';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import ReusableSnackbar from '../../reusableComponent/ReusableSnackbar';
import customermaster from '../../../services/masters/customerMaster.Service';
import CustomerStatment from '../../../services/report/CustomerStatment';
import { useSelector } from 'react-redux';

dayjs.extend(customParseFormat);

const printStyles = `
  @media print {
    body * {
      visibility: hidden;
    }
    .printable-area, .printable-area * {
      visibility: visible;
    }
    .printable-area {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
    }
    .no-print {
      display: none !important;
    }
    .printable-table {
      width: 100%;
      border-collapse: collapse;
    }
    .printable-table th, .printable-table td {
      padding: 8px;
      font-size: 12px;
    }
  }
`;

const CustomerStatement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const editData = location.state || null;

  const login_details = sessionStorage.getItem('loginUser');
  const userD = JSON.parse(login_details);
  const userInfo = userD.result.userInfo;
  const unit_id = userInfo.depot_id || userInfo.plant_id;

  const [successMessage, setSuccessMessage] = useState('');
  const [displayData, setDisplayData] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openProductDialog, setOpenProductDialog] = useState(false);
  const [productDialogData, setProductDialogData] = useState({
    delivered_cylinders: [],
    collected_cylinders: [],
    pending_cylinders_with_customer: [],
    main_prod_code: '',
  });
  const [productDialogTitle, setProductDialogTitle] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const defaultRowsPerPage = useSelector(state => state.eci.rows_per_page || 15);

  const [controller, setController] = useState({
    page: 0,
    rowsPerPage: defaultRowsPerPage,
  });

  const allCustomersOption = { cust_id: 'all', cust_name: 'All' };
  const customerOptions = [allCustomersOption, ...customers];

  const { control, watch, formState: { errors, isValid } } = useForm({
    defaultValues: {
      entry_date_from: editData?.entry_date ? dayjs(editData.entry_date).format('YYYY-MM-DD') : '',
      entry_date_to: '',
      customer: editData?.customer || '',
    },
    resolver: async (data) => {
      const errors = {};
      if (!data.entry_date_from) errors.entry_date_from = { message: 'From Date is required' };
      if (!data.entry_date_to) errors.entry_date_to = { message: 'To Date is required' };
      if (data.entry_date_from && data.entry_date_to && dayjs(data.entry_date_to).isBefore(dayjs(data.entry_date_from))) {
        errors.entry_date_to = { message: 'To Date cannot be before From Date' };
      }
      return { values: data, errors };
    },
  });

  const entryDateFrom = watch('entry_date_from');
  const entryDateTo = watch('entry_date_to');
  const customer = watch('customer');

  useEffect(() => {
    async function fetchCustomerData() {
      try {
        setLoading(true);
        const customerData = await customermaster.getCustomerList({ page: 0, size: 999, searchKey: '' });
        console.log('Customer Data:', customerData);
        setCustomers(customerData || []);
      } catch (error) {
        console.error('Error fetching customer data:', error);
        setSuccessMessage('Error fetching customer data');
        setCustomers([]);
      } finally {
        setLoading(false);
      }
    }
    fetchCustomerData();

    const styleSheet = document.createElement('style');
    styleSheet.type = 'text/css';
    styleSheet.innerText = printStyles;
    document.head.appendChild(styleSheet);

    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  useEffect(() => {
    if (isValid && entryDateFrom && entryDateTo) {
      fetchData(
        {
          entry_date_from: entryDateFrom,
          entry_date_to: entryDateTo,
          customer: customer,
        },
        controller.page,
        controller.rowsPerPage
      );
    }
  }, [entryDateFrom, entryDateTo, customer, controller.page, controller.rowsPerPage, isValid]);

  const fetchData = async (formData, page, rowsPerPage) => {
    try {
      setLoading(true);
      setDisplayData([]);

      const filterParams = {
        page,
        size: rowsPerPage,
        start_date: dayjs(formData.entry_date_from).format('YYYY-MM-DD'),
        end_date: dayjs(formData.entry_date_to).format('YYYY-MM-DD'),
        customer_id: formData.customer === 'all' ? '' : formData.customer || '',
      };

      console.log('API filterParams:', filterParams);
      const response = await CustomerStatment.getCustomerStatementreport(filterParams);
      console.log('API Response:', response);
      const fetchedData = response?.data || response || [];
      const total = response?.total_count || fetchedData.length || 0;

      const flattenedData = fetchedData.map((item, index) => {
        const customer = customers.find(c => c.cust_id === item.customer_id) || { cust_name: 'N/A' };
        // Use collected_cylinders for prod_code fallback if delivered_cylinders is empty
        const prod_code = item.prod_code || (item.delivered_cylinders?.[0]?.prod_code) || (item.collected_cylinders?.[0]?.prod_code) || 'N/A';
        return {
          sr_no: page * rowsPerPage + index + 1,
          customer_id: item.customer_id || 'N/A',
          date: item.start_date ? dayjs(item.start_date, ['YYYY-M-D', 'YYYY-MM-DD']).format('YYYY-MM-DD') : 'N/A',
          cust_name: customer.cust_name,
          prod_code,
          delivered_count: item.delivered_count || 0,
          collected_count: item.collected_count || 0,
          net_pending: item.net_pending || 0,
          delivered_cylinders: item.delivered_cylinders || [],
          collected_cylinders: item.collected_cylinders || [],
          pending_cylinders_with_customer: item.pending_cylinders_with_customer || [],
        };
      });

      console.log('Flattened Data:', flattenedData);
      const filteredData = formData.customer && formData.customer !== 'all'
        ? flattenedData.filter(row => row.customer_id === formData.customer)
        : flattenedData;

      setDisplayData(filteredData);
      setTotalCount(total);
    } catch (error) {
      console.error('Error fetching data for display:', error);
      setSuccessMessage(error.response?.data?.message || 'Error fetching report data');
      setDisplayData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setController(prev => ({ ...prev, page: newPage }));
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setController({
      page: 0,
      rowsPerPage: newRowsPerPage,
    });
  };

  const handleProductClick = (row) => {
    console.log('Product Dialog Data:', row);
    setProductDialogData({
      delivered_cylinders: row.delivered_cylinders || [],
      collected_cylinders: row.collected_cylinders || [],
      pending_cylinders_with_customer: row.pending_cylinders_with_customer || [],
      main_prod_code: row.prod_code,
    });
    setProductDialogTitle(`Cylinder Details for Product Code: ${row.prod_code}`);
    setOpenProductDialog(true);
  };

  const handleCloseProductDialog = () => {
    setOpenProductDialog(false);
    setProductDialogData({
      delivered_cylinders: [],
      collected_cylinders: [],
      pending_cylinders_with_customer: [],
      main_prod_code: '',
    });
    setProductDialogTitle('');
  };

  const handlePrint = () => {
    window.print();
  };

  const dateConversion = (date) => {
    if (!date) return '';
    const parsedDate = dayjs(date, ['YYYY-M-D', 'YYYY-MM-DD']);
    if (!parsedDate.isValid()) return '';
    return parsedDate.format('DD/MM/YYYY');
  };

  // Check if any cylinder has a product description
  const hasProductDescription = productDialogData.delivered_cylinders.some(row => row.prod_description) ||
    productDialogData.collected_cylinders.some(row => row.prod_description) ||
    productDialogData.pending_cylinders_with_customer.some(row => row.prod_description);

  return (
    <Box className={styles.cts_page} sx={{ padding: '0px !important', margin: '0px !important' }}>
      <Box className={`${styles.body} printable-area`}>
        <Breadcrumb routeSegments={[{ name: 'Customer Statement' }]} />
        <Typography className={styles.header_txt}>CUSTOMER STATEMENT</Typography>

        <form className="no-print">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4.5}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography className={styles.label_Txt_add}>Customer</Typography>
                {loading ? (
                  <Typography>Loading...</Typography>
                ) : (
                  <Controller
                    name="customer"
                    control={control}
                    render={({ field }) => (
                      <Autocomplete
                        disablePortal
                        options={customerOptions}
                        getOptionLabel={(option) => option.cust_name || ''}
                        isOptionEqualToValue={(option, value) => option.cust_id === value.cust_id}
                        value={customerOptions.find((c) => c.cust_id === field.value) || null}
                        onChange={(_, value) => field.onChange(value ? value.cust_id : '')}
                        loading={loading}
                        renderOption={(props, option) => (
                          <li {...props} className={styles.cts_label}>
                            {option.cust_name}
                          </li>
                        )}
                        renderInput={(params) => (
                          <CTSinput
                            {...params}
                            placeholder="Select or type a customer"
                            variant="outlined"
                            size="small"
                            fullWidth
                            error={!!errors.customer}
                            helperText={errors.customer?.message}
                            InputLabelProps={{ shrink: true }}
                            InputProps={{
                              ...params.InputProps,
                              className: styles.CTSinput_Box,
                            }}
                          />
                        )}
                        sx={{ width: '100%' }}
                      />
                    )}
                  />
                )}
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography className={styles.label_Txt_add}>From Date</Typography>
                <Controller
                  name="entry_date_from"
                  control={control}
                  rules={{ required: 'From Date is required' }}
                  render={({ field }) => (
                    <CTSRinput
                      size="small"
                      fullWidth
                      type="date"
                      {...field}
                      error={!!errors.entry_date_from}
                      helperText={errors.entry_date_from?.message}
                      InputProps={{ className: styles.CTSinput_Box }}
                    />
                  )}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography className={styles.label_Txt_add}>To Date</Typography>
                <Controller
                  name="entry_date_to"
                  control={control}
                  rules={{ required: 'To Date is required' }}
                  render={({ field }) => (
                    <CTSRinput
                      size="small"
                      fullWidth
                      type="date"
                      {...field}
                      error={!!errors.entry_date_to}
                      helperText={errors.entry_date_to?.message}
                      InputProps={{ className: styles.CTSinput_Box }}
                    />
                  )}
                />
              </Box>
            </Grid>
          </Grid>
        </form>

        <Grid item xs={12} sx={{ mt: 4 }}>
          <Paper sx={{ width: '100%' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                padding: '10px',
              }}
              className="no-print"
            >
              <Button
                style={{ float: 'right', cursor: 'pointer' }}
                variant="outlined"
                startIcon={<Print />}
                onClick={handlePrint}
                disabled={displayData.length === 0}
              >
                Print
              </Button>
            </Box>
            <TableContainer component={Paper} className={`${styles.list_tbl_container} printable-table`}>
              <Table size="small" stickyHeader className="printable-table">
                <TableHead className={styles.table_head_container}>
                  <StyledTableRow>
                    <StyledTableCell align="center">Sr No</StyledTableCell>
                  
                  
                    <StyledTableCell align="left">Product</StyledTableCell>
                    <StyledTableCell align="left">Delivered Stock</StyledTableCell>
                    <StyledTableCell align="left">Returning Stock</StyledTableCell>
                    <StyledTableCell align="left">Pending Stock</StyledTableCell>
                  </StyledTableRow>
                </TableHead>
                <TableBody sx={{ backgroundColor: '#fff' }}>
                  {loading ? (
                    <StyledTableRow>
                      <StyledTableCell colSpan={7} align="center">
                        <Typography>Loading data...</Typography>
                      </StyledTableCell>
                    </StyledTableRow>
                  ) : displayData.length === 0 ? (
                    <StyledTableRow>
                      <StyledTableCell colSpan={7} align="center">
                        <Typography>No data found</Typography>
                      </StyledTableCell>
                    </StyledTableRow>
                  ) : (
                    displayData.map((row, index) => (
                      <StyledTableRow key={index}>
                        <StyledTableCell align="center" className={styles.inspection_cell}>{row.sr_no}</StyledTableCell>
                      
                        <StyledTableCell
                          className={styles.inspection_cell}
                          onClick={() => handleProductClick(row)}
                          style={{ cursor: 'pointer', color: '#1976d2' }}
                        >
                          {row.prod_code}
                        </StyledTableCell>
                        <StyledTableCell className={styles.inspection_cell}>{row.delivered_count}</StyledTableCell>
                        <StyledTableCell className={styles.inspection_cell}>{row.collected_count}</StyledTableCell>
                        <StyledTableCell className={styles.inspection_cell}>{row.net_pending}</StyledTableCell>
                      </StyledTableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 100]}
              component="div"
              count={totalCount}
              rowsPerPage={controller.rowsPerPage}
              page={controller.page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              className="no-print"
            />
          </Paper>
        </Grid>

        <Dialog
          PaperProps={{
            sx: {
              minWidth: '90%',
              maxHeight: '90%',
            },
          }}
          onClose={handleCloseProductDialog}
          open={openProductDialog}
        >
          <DialogTitle id="product-dialog-title">
            {productDialogTitle}
            <IconButton
              aria-label="close"
              onClick={handleCloseProductDialog}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Delivered Cylinders</Typography>
            <TableContainer component={Paper} className={`${styles.list_tbl_container} printable-table`}>
              <Table size="small" stickyHeader className="printable-table">
                <TableHead className={styles.table_head_container}>
                  <StyledTableRow>
                    <StyledTableCell align="center">Sr No</StyledTableCell>
                    <StyledTableCell align="left">Cylinder No</StyledTableCell>
                    <StyledTableCell align="left">Product Code</StyledTableCell>
                    {hasProductDescription && <StyledTableCell align="left">Product Description</StyledTableCell>}
                    <StyledTableCell align="left">Entry No</StyledTableCell>
                    <StyledTableCell align="left">Entry Date</StyledTableCell>
                  </StyledTableRow>
                </TableHead>
                <TableBody sx={{ backgroundColor: '#fff' }}>
                  {productDialogData.delivered_cylinders.length > 0 ? (
                    productDialogData.delivered_cylinders.map((row, index) => (
                      <StyledTableRow key={index}>
                        <StyledTableCell align="center">{index + 1}</StyledTableCell>
                        <StyledTableCell className={styles.inspection_cell}>{row.cyl_no || '-'}</StyledTableCell>
                        <StyledTableCell className={styles.inspection_cell}>{row.prod_code || productDialogData.main_prod_code || '-'}</StyledTableCell>
                        {hasProductDescription && (
                          <StyledTableCell className={styles.inspection_cell}>{row.prod_description || '-'}</StyledTableCell>
                        )}
                        <StyledTableCell className={styles.inspection_cell}>{row.entry_no || '-'}</StyledTableCell>
                        <StyledTableCell className={styles.inspection_cell}>{dateConversion(row.entry_date) || '-'}</StyledTableCell>
                      </StyledTableRow>
                    ))
                  ) : (
                    <StyledTableRow>
                      <StyledTableCell colSpan={hasProductDescription ? 6 : 5} align="center">
                        No delivered cylinders available
                      </StyledTableCell>
                    </StyledTableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Collected Cylinders</Typography>
            <TableContainer component={Paper} className={`${styles.list_tbl_container} printable-table`}>
              <Table size="small" stickyHeader className="printable-table">
                <TableHead className={styles.table_head_container}>
                  <StyledTableRow>
                    <StyledTableCell align="center">Sr No</StyledTableCell>
                    <StyledTableCell align="left">Cylinder No</StyledTableCell>
                    <StyledTableCell align="left">Product Code</StyledTableCell>
                    {hasProductDescription && <StyledTableCell align="left">Product Description</StyledTableCell>}
                    <StyledTableCell align="left">Entry No</StyledTableCell>
                    <StyledTableCell align="left">Entry Date</StyledTableCell>
                  </StyledTableRow>
                </TableHead>
                <TableBody sx={{ backgroundColor: '#fff' }}>
                  {productDialogData.collected_cylinders.length > 0 ? (
                    productDialogData.collected_cylinders.map((row, index) => (
                      <StyledTableRow key={index}>
                        <StyledTableCell align="center">{index + 1}</StyledTableCell>
                        <StyledTableCell className={styles.inspection_cell}>{row.cyl_no || '-'}</StyledTableCell>
                        <StyledTableCell className={styles.inspection_cell}>{row.prod_code || productDialogData.main_prod_code || '-'}</StyledTableCell>
                        {hasProductDescription && (
                          <StyledTableCell className={styles.inspection_cell}>{row.prod_description || '-'}</StyledTableCell>
                        )}
                        <StyledTableCell className={styles.inspection_cell}>{row.entry_no || '-'}</StyledTableCell>
                        <StyledTableCell className={styles.inspection_cell}>{dateConversion(row.entry_date) || '-'}</StyledTableCell>
                      </StyledTableRow>
                    ))
                  ) : (
                    <StyledTableRow>
                      <StyledTableCell colSpan={hasProductDescription ? 6 : 5} align="center">
                        No collected cylinders available
                      </StyledTableCell>
                    </StyledTableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Pending Cylinders with Customer</Typography>
            <TableContainer component={Paper} className={`${styles.list_tbl_container} printable-table`}>
              <Table size="small" stickyHeader className="printable-table">
                <TableHead className={styles.table_head_container}>
                  <StyledTableRow>
                    <StyledTableCell align="center">Sr No</StyledTableCell>
                    <StyledTableCell align="left">Cylinder No</StyledTableCell>
                    <StyledTableCell align="left">Product Code</StyledTableCell>
                    {hasProductDescription && <StyledTableCell align="left">Product Description</StyledTableCell>}
                    <StyledTableCell align="left">Entry No</StyledTableCell>
                    <StyledTableCell align="left">Entry Date</StyledTableCell>
                  </StyledTableRow>
                </TableHead>
                <TableBody sx={{ backgroundColor: '#fff' }}>
                  {productDialogData.pending_cylinders_with_customer.length > 0 ? (
                    productDialogData.pending_cylinders_with_customer.map((row, index) => (
                      <StyledTableRow key={index}>
                        <StyledTableCell align="center">{index + 1}</StyledTableCell>
                        <StyledTableCell className={styles.inspection_cell}>{row.cyl_no || '-'}</StyledTableCell>
                        <StyledTableCell className={styles.inspection_cell}>{row.prod_code || productDialogData.main_prod_code || '-'}</StyledTableCell>
                        {hasProductDescription && (
                          <StyledTableCell className={styles.inspection_cell}>{row.prod_description || '-'}</StyledTableCell>
                        )}
                        <StyledTableCell className={styles.inspection_cell}>{row.entry_no || '-'}</StyledTableCell>
                        <StyledTableCell className={styles.inspection_cell}>{dateConversion(row.entry_date) || '-'}</StyledTableCell>
                      </StyledTableRow>
                    ))
                  ) : (
                    <StyledTableRow>
                      <StyledTableCell colSpan={hasProductDescription ? 6 : 5} align="center">
                        No pending cylinders available
                      </StyledTableCell>
                    </StyledTableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
        </Dialog>

        <ReusableSnackbar
          open={!!successMessage}
          onClose={() => setSuccessMessage('')}
          message={successMessage}
          autoHideDuration={2000}
          vertical="top"
          horizontal="right"
          className="no-print"
        />
      </Box>
    </Box>
  );
};

export default CustomerStatement;